import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { writeFetch, getSession } from '../../api';

const {
  FiCheck, FiX, FiInfo, FiMail, FiBriefcase, FiShoppingBag, FiCalendar, FiUser, FiRefreshCw,
  FiClock, FiDollarSign, FiTrendingUp, FiUsers, FiStar, FiSliders, FiSave, FiSearch,
  FiCheckCircle, FiXCircle, FiPackage, FiPercent, FiToggleLeft, FiToggleRight, FiTrash2,
  FiAlertCircle, FiCreditCard, FiMapPin, FiPhone, FiMessageCircle, FiBook, FiFilter
} = FiIcons;

const STORAGE_KEY = 'em_merchant_commission';
const BOOKINGS_TABLE = 'service_bookings_1777866100042';

const loadCommission = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch { return {}; }
};

const CATEGORY_RATES = [
  { id: 'memorial_gifts',   label: 'Memorial Gifts',   rate: 10, color: 'bg-accent-navy' },
  { id: 'digital_services', label: 'Digital Services', rate: 12, color: 'bg-violet-600'  },
  { id: 'floral_nature',    label: 'Floral & Nature',  rate: 8,  color: 'bg-green-600'   },
  { id: 'funeral_services', label: 'Funeral Services', rate: 7,  color: 'bg-blue-600'    },
  { id: 'photography',      label: 'Photography',      rate: 11, color: 'bg-rose-600'    },
  { id: 'engraving',        label: 'Engraving',        rate: 9,  color: 'bg-amber-600'   },
];

const SectionCard = ({ icon, title, subtitle, children, badge }) => (
  <div className="bg-white rounded-[32px] border border-brand-dark p-8 space-y-6">
    <div className="flex items-center justify-between pb-5 border-b border-brand-dark">
      <div className="flex items-center gap-4">
        <div className="p-3 rounded-2xl bg-brand-light">
          <SafeIcon icon={icon} className="w-5 h-5 text-accent-navy" />
        </div>
        <div>
          <h3 className="font-serif text-lg text-accent-navy">{title}</h3>
          {subtitle && <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">{subtitle}</p>}
        </div>
      </div>
      {badge !== undefined && badge > 0 && (
        <div className="bg-accent-gold/10 border border-accent-gold/30 px-4 py-2 rounded-xl flex items-center gap-2">
          <SafeIcon icon={FiClock} className="text-accent-gold w-3.5 h-3.5" />
          <span className="text-[10px] font-black uppercase tracking-widest text-accent-navy">{badge} Pending</span>
        </div>
      )}
    </div>
    {children}
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    active:    { bg: 'bg-green-100',  text: 'text-green-700',  dot: 'bg-green-500',  label: 'Active'    },
    suspended: { bg: 'bg-red-100',    text: 'text-red-700',    dot: 'bg-red-500',    label: 'Suspended' },
    pending_admin: { bg: 'bg-amber-100',  text: 'text-amber-700',  dot: 'bg-amber-500',  label: 'Pending Admin' },
    pending_merchant: { bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500', label: 'Pending Merchant' },
    pending:   { bg: 'bg-amber-100',  text: 'text-amber-700',  dot: 'bg-amber-500',  label: 'Pending'   },
    approved:  { bg: 'bg-blue-100',   text: 'text-blue-700',   dot: 'bg-blue-500',   label: 'Approved'  },
    rejected:  { bg: 'bg-gray-100',   text: 'text-gray-500',   dot: 'bg-gray-400',   label: 'Rejected'  },
    cancelled: { bg: 'bg-gray-100',   text: 'text-gray-500',   dot: 'bg-gray-400',   label: 'Cancelled' },
    confirmed: { bg: 'bg-green-100',  text: 'text-green-700',  dot: 'bg-green-500',  label: 'Confirmed' },
    completed: { bg: 'bg-purple-100', text: 'text-purple-700', dot: 'bg-purple-500', label: 'Completed' }
  };
  const s = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${s.bg} ${s.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
};

// ── BOOKING OVERSIGHT PANEL ─────────────────────────────────────────────
const BookingOversightPanel = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [filterMerchant, setFilterMerchant] = useState('');
  const [merchants, setMerchants] = useState([]);
  const [autoApprove, setAutoApprove] = useState(() => localStorage.getItem('admin_auto_approve_bookings') === 'true');

  const toggleAutoApprove = (val) => {
    setAutoApprove(val);
    localStorage.setItem('admin_auto_approve_bookings', val);
  };

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await writeFetch(`read/${BOOKINGS_TABLE}?limit=500`);
      const data = await res.json();
      
      let allBookings = [];
      if (data.status === 'success' && data.data?.length > 0) {
        allBookings = data.data;
      }
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('mock_bookings_')) {
          try {
            const mockBookings = JSON.parse(localStorage.getItem(key));
            if (Array.isArray(mockBookings)) {
              allBookings = [...allBookings, ...mockBookings];
            }
          } catch(e) {}
        }
      }
      
      const uniqueBookings = allBookings.filter((booking, index, self) => 
        index === self.findIndex(b => b.id === booking.id)
      ).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      setBookings(uniqueBookings);
      
      const merchantsRes = await writeFetch('read/merchant_onboarding_codes_1776600001?limit=100');
      const merchantsData = await merchantsRes.json();
      if (merchantsData.status === 'success') {
        setMerchants(merchantsData.data || []);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await writeFetch(`update/${BOOKINGS_TABLE}/${bookingId}`, {
        method: 'PUT', body: JSON.stringify({ status: newStatus }),
      });
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('mock_bookings_')) {
          const mockBookings = JSON.parse(localStorage.getItem(key) || '[]');
          const updated = mockBookings.map(b => b.id === bookingId ? { ...b, status: newStatus } : b);
          localStorage.setItem(key, JSON.stringify(updated));
        }
      }
      
      fetchBookings();
    } catch (error) {
      console.error('Error updating booking status:', error);
    }
  };

  const getMerchantName = (merchantId) => {
    const merchant = merchants.find(m => m.owner_id === merchantId || m.id === merchantId);
    return merchant?.business_name || 'Unknown Merchant';
  };

  const filteredBookings = bookings.filter(b => {
    let effectiveStatus = b.status;
    if (b.status === 'pending') effectiveStatus = 'pending_admin';
    if (b.status === 'approved') effectiveStatus = 'pending_merchant';

    const matchesStatus = filterStatus === 'ALL' || effectiveStatus === filterStatus;
    const matchesMerchant = !filterMerchant || 
      getMerchantName(b.merchant_id).toLowerCase().includes(filterMerchant.toLowerCase());
    return matchesStatus && matchesMerchant;
  });

  const statusStats = {
    all: bookings.length,
    pending_admin: bookings.filter(b => b.status === 'pending_admin' || b.status === 'pending').length,
    pending_merchant: bookings.filter(b => b.status === 'pending_merchant' || b.status === 'approved').length,
    confirmed: bookings.filter(b => b.status === 'confirmed').length,
    cancelled: bookings.filter(b => b.status === 'cancelled').length
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-4 border-accent-gold border-t-accent-navy rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white p-4 rounded-2xl border border-brand-dark text-center">
          <p className="text-2xl font-serif text-accent-navy">{statusStats.all}</p>
          <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Total</p>
        </div>
        <div className="bg-amber-50 p-4 rounded-2xl border border-amber-200 text-center">
          <p className="text-2xl font-serif text-amber-700">{statusStats.pending_admin}</p>
          <p className="text-[10px] font-black uppercase tracking-widest text-amber-600">Pending Admin</p>
        </div>
        <div className="bg-blue-50 p-4 rounded-2xl border border-blue-200 text-center">
          <p className="text-2xl font-serif text-blue-700">{statusStats.pending_merchant}</p>
          <p className="text-[10px] font-black uppercase tracking-widest text-blue-600">Pending Merchant</p>
        </div>
        <div className="bg-green-50 p-4 rounded-2xl border border-green-200 text-center">
          <p className="text-2xl font-serif text-green-700">{statusStats.confirmed}</p>
          <p className="text-[10px] font-black uppercase tracking-widest text-green-600">Confirmed</p>
        </div>
        <div className="bg-gray-100 p-4 rounded-2xl border border-gray-200 text-center">
          <p className="text-2xl font-serif text-gray-500">{statusStats.cancelled}</p>
          <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Cancelled</p>
        </div>
      </div>

      {/* Filters and Toggle */}
      <div className="bg-white p-4 rounded-2xl border border-brand-dark mb-6">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex-1 w-full relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
            <input
              type="text"
              placeholder="Search by merchant name..."
              value={filterMerchant}
              onChange={(e) => setFilterMerchant(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-brand-light border border-brand-dark rounded-xl text-sm focus:border-accent-gold outline-none"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto w-full md:w-auto pb-1">
            <span className="text-[10px] font-black uppercase tracking-widest text-text-muted self-center mr-2">Status:</span>
            {['ALL', 'pending_admin', 'pending_merchant', 'confirmed', 'cancelled'].map(status => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest border transition-all whitespace-nowrap ${
                  filterStatus === status
                    ? 'bg-accent-navy text-white border-accent-navy'
                    : 'bg-brand-light text-text-muted border-brand-dark hover:border-accent-gold'
                }`}
              >
                {status.replace('_', ' ')}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
           <div>
            <p className="text-[12px] font-bold text-accent-navy">Global Auto-Approve</p>
            <p className="text-[10px] text-text-muted mt-0.5 uppercase tracking-widest">Automatically devolve bookings to merchants without admin review</p>
          </div>
          <button onClick={() => toggleAutoApprove(!autoApprove)} type="button"
            className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 shadow-inner ${autoApprove ? 'bg-green-500' : 'bg-gray-200'}`}>
            <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${autoApprove ? 'translate-x-7' : 'translate-x-1'}`} />
          </button>
        </div>
      </div>

      {/* Bookings List */}
      <SectionCard icon={FiCalendar} title="All Bookings" subtitle="Cross-merchant booking management">
        {filteredBookings.length === 0 ? (
          <div className="py-16 text-center bg-brand-light rounded-[24px] border-2 border-dashed border-brand-dark">
            <SafeIcon icon={FiCalendar} className="w-12 h-12 text-text-muted mx-auto mb-4" />
            <h4 className="text-lg font-serif text-accent-navy mb-1">No Bookings Found</h4>
            <p className="text-text-muted text-sm font-light">No bookings match your current filters.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredBookings.map(b => (
              <div key={b.id} className="bg-white p-5 rounded-[24px] border border-brand-dark flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 hover:border-accent-gold transition-all shadow-sm">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <StatusBadge status={b.status} />
                    <p className="font-bold text-accent-navy truncate">{b.listing_title}</p>
                    {b.pricing_model === 'free' ? (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-[9px] font-black uppercase">FREE</span>
                    ) : (
                      <span className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-[9px] font-black uppercase border border-blue-100">PAID</span>
                    )}
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-4 text-sm text-text-muted">
                    <div className="flex items-center gap-1.5">
                      <SafeIcon icon={FiUser} className="w-3.5 h-3.5" /> 
                      <span className="font-medium text-accent-navy">{b.customer_name}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <SafeIcon icon={FiMapPin} className="w-3.5 h-3.5" /> 
                      <span className="truncate max-w-[200px]">{getMerchantName(b.merchant_id)}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <SafeIcon icon={FiClock} className="w-3.5 h-3.5" /> 
                      <span>{new Date(b.created_at).toLocaleString()}</span>
                    </div>
                    {b.price > 0 && (
                      <div className="flex items-center gap-1.5">
                        <SafeIcon icon={FiDollarSign} className="w-3.5 h-3.5 text-green-500" /> 
                        <span className="font-bold text-green-600">${parseFloat(b.price).toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                  
                  {b.start_time && (
                    <div className="mt-2 text-xs text-text-muted flex items-center gap-1.5">
                      <SafeIcon icon={FiCalendar} className="w-3.5 h-3.5" />
                      <span>Scheduled: {new Date(b.start_time).toLocaleString()}</span>
                    </div>
                  )}
                </div>
                
                <div className="flex gap-2 w-full lg:w-auto shrink-0 border-t lg:border-t-0 border-brand-dark pt-4 lg:pt-0">
                  {(b.status === 'pending_admin' || b.status === 'pending') && (
                    <>
                      <button onClick={() => updateBookingStatus(b.id, 'rejected')}
                        className="flex-1 lg:flex-none px-4 py-2.5 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-all flex items-center justify-center text-[10px] font-black uppercase tracking-widest">
                        Reject
                      </button>
                      <button onClick={() => updateBookingStatus(b.id, 'pending_merchant')}
                        className="flex-1 lg:flex-none px-4 py-2.5 bg-green-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-green-600 transition-all flex items-center justify-center gap-2 shadow-md">
                        <SafeIcon icon={FiCheck} className="w-4 h-4" /> Devolve to Merchant
                      </button>
                    </>
                  )}
                  {(b.status === 'pending_merchant' || b.status === 'approved') && (
                    <button onClick={() => updateBookingStatus(b.id, 'confirmed')}
                      className="flex-1 lg:flex-none px-4 py-2.5 bg-blue-50 text-blue-600 rounded-xl hover:bg-blue-100 transition-all flex items-center justify-center text-[10px] font-black uppercase tracking-widest border border-blue-200">
                      <SafeIcon icon={FiCheckCircle} className="w-4 h-4" /> Force Confirm
                    </button>
                  )}
                  {b.status === 'confirmed' && (
                    <button onClick={() => updateBookingStatus(b.id, 'completed')}
                      className="flex-1 lg:flex-none px-4 py-2.5 bg-purple-50 text-purple-600 rounded-xl hover:bg-purple-100 transition-all flex items-center justify-center text-[10px] font-black uppercase tracking-widest border border-purple-200">
                      <SafeIcon icon={FiCheckCircle} className="w-4 h-4" /> Complete
                    </button>
                  )}
                  {(b.status === 'pending_merchant' || b.status === 'approved' || b.status === 'confirmed') && (
                    <button onClick={() => updateBookingStatus(b.id, 'cancelled')}
                      className="flex-1 lg:flex-none px-4 py-2.5 bg-gray-50 text-gray-500 rounded-xl hover:bg-gray-100 transition-all flex items-center justify-center text-[10px] font-black uppercase tracking-widest border border-gray-200">
                      <SafeIcon icon={FiX} className="w-4 h-4" /> Cancel
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
};

// ── PRODUCT APPROVALS PANEL ───────────────────────────────────────────────────
const ProductApprovalsPanel = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoApprove, setAutoApprove] = useState(() => localStorage.getItem('admin_auto_approve_products') === 'true');

  const toggleAutoApprove = (val) => {
    setAutoApprove(val);
    localStorage.setItem('admin_auto_approve_products', val);
  };

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    let allProducts = [];
    try {
      const res = await writeFetch('read/merchant_products_1776540001?limit=200');
      const data = await res.json();
      if (data.status === 'success') {
        allProducts = data.data || [];
      }
    } catch { /* silent */ }

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('mock_products_')) {
        try {
          const parsed = JSON.parse(localStorage.getItem(key));
          if (Array.isArray(parsed)) {
            for (const p of parsed) {
              if (!allProducts.find(x => x.id === p.id)) {
                allProducts.push(p);
              }
            }
          }
        } catch (e) {}
      }
    }

    setProducts(allProducts.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    setLoading(false);
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const handleApprove = async (id) => {
    try {
      await writeFetch(`update/merchant_products_1776540001/${id}`, {
        method: 'PUT', body: JSON.stringify({ approval_status: 'approved' })
      });
    } catch {}
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('mock_products_')) {
        const items = JSON.parse(localStorage.getItem(key) || '[]');
        const updated = items.map(p => p.id === id ? { ...p, approval_status: 'approved' } : p);
        localStorage.setItem(key, JSON.stringify(updated));
      }
    }
    fetchProducts();
  };

  const handleReject = async (id) => {
    try {
      await writeFetch(`update/merchant_products_1776540001/${id}`, {
        method: 'PUT', body: JSON.stringify({ approval_status: 'rejected' })
      });
    } catch {}
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('mock_products_')) {
        const items = JSON.parse(localStorage.getItem(key) || '[]');
        const updated = items.map(p => p.id === id ? { ...p, approval_status: 'rejected' } : p);
        localStorage.setItem(key, JSON.stringify(updated));
      }
    }
    fetchProducts();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await writeFetch(`delete/merchant_products_1776540001/${id}`, {
        method: 'DELETE'
      });
    } catch {}
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('mock_products_')) {
        const items = JSON.parse(localStorage.getItem(key) || '[]');
        const updated = items.filter(p => p.id !== id);
        localStorage.setItem(key, JSON.stringify(updated));
      }
    }
    fetchProducts();
  };

  const pending = products.filter(p => p.approval_status === 'pending');
  const processed = products.filter(p => p.approval_status !== 'pending');

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex justify-between items-center bg-white p-4 border border-brand-dark rounded-2xl shadow-sm">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-text-muted">
              Product Queue
            </div>
            <p className="text-2xl font-serif text-accent-navy">{pending.length}</p>
          </div>
          <button onClick={fetchProducts} className="p-3 bg-brand-light border border-brand-dark rounded-xl text-text-muted hover:border-accent-gold transition-all">
            <SafeIcon icon={FiRefreshCw} className="w-5 h-5" />
          </button>
        </div>

        <div className="flex items-center justify-between p-4 bg-white border border-brand-dark rounded-2xl shadow-sm">
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Auto-Approve Setting</p>
            <p className="text-xs text-text-muted font-light mt-1">Automatically approve products without review.</p>
          </div>
          <button onClick={() => toggleAutoApprove(!autoApprove)} type="button"
            className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${autoApprove ? 'bg-green-500' : 'bg-gray-200'}`}>
            <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${autoApprove ? 'translate-x-7' : 'translate-x-1'}`} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="w-8 h-8 border-4 border-accent-gold border-t-accent-navy rounded-full animate-spin" />
        </div>
      ) : (
        <SectionCard icon={FiPackage} title="Pending Approvals" subtitle="Products awaiting review" badge={pending.length}>
          {pending.length === 0 ? (
            <div className="py-16 text-center bg-brand-light rounded-[24px] border-2 border-dashed border-brand-dark">
              <SafeIcon icon={FiCheckCircle} className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h4 className="text-lg font-serif text-accent-navy mb-1">Queue Clear</h4>
              <p className="text-text-muted text-sm font-light">All merchant products have been reviewed.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {pending.map(p => (
                <div key={p.id} className="bg-brand-light p-5 rounded-[24px] border border-brand-dark flex flex-col md:flex-row items-center gap-6">
                  <img src={p.image_url} alt={p.name} className="w-20 h-20 rounded-xl object-cover border border-brand-dark" />
                  <div className="flex-1">
                    <h4 className="font-bold text-accent-navy">{p.name}</h4>
                    <p className="text-[10px] text-text-muted uppercase tracking-widest mt-1">${p.price} · {p.category}</p>
                    <p className="text-xs text-text-muted mt-2 line-clamp-2">{p.description}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => handleReject(p.id)} className="p-3 bg-white text-red-500 rounded-xl border border-brand-dark hover:bg-red-50 hover:border-red-200 transition-all">
                      <SafeIcon icon={FiX} className="w-5 h-5" />
                    </button>
                    <button onClick={() => handleApprove(p.id)} className="px-5 py-3 bg-green-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-green-600 transition-all flex items-center gap-2">
                      <SafeIcon icon={FiCheckCircle} className="w-4 h-4" /> Approve
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </SectionCard>
      )}

      {processed.length > 0 && (
         <SectionCard icon={FiCheckCircle} title="Processed Products" subtitle="Recently reviewed">
           <div className="space-y-3">
             {processed.map(p => (
               <div key={p.id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 bg-white rounded-2xl border border-brand-dark gap-4">
                 <div className="flex items-center gap-4">
                   <img src={p.image_url} alt={p.name} className="w-10 h-10 rounded-lg object-cover" />
                   <div>
                     <p className="font-bold text-accent-navy text-sm">{p.name}</p>
                     <p className="text-[9px] text-text-muted uppercase tracking-widest">{p.category}</p>
                   </div>
                 </div>
                 <div className="flex items-center gap-4 w-full sm:w-auto justify-between sm:justify-end">
                   <StatusBadge status={p.approval_status} />
                   <div className="flex items-center gap-3">
                      {p.approval_status === 'approved' && (
                        <button onClick={() => handleReject(p.id)} className="px-3 py-1.5 bg-amber-50 text-amber-600 border border-amber-200 rounded-lg text-[9px] font-black uppercase tracking-widest hover:bg-amber-100 transition-all">Unapprove</button>
                      )}
                      {p.approval_status === 'rejected' && (
                        <button onClick={() => handleApprove(p.id)} className="px-3 py-1.5 bg-green-50 text-green-600 border border-green-200 rounded-lg text-[9px] font-black uppercase tracking-widest hover:bg-green-100 transition-all">Approve</button>
                      )}
                      <button onClick={() => handleDelete(p.id)} className="p-2 text-text-muted hover:text-red-500 hover:bg-red-50 rounded-lg transition-all" title="Delete Product">
                        <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                      </button>
                   </div>
                 </div>
               </div>
             ))}
           </div>
         </SectionCard>
      )}
    </div>
  );
};

// ── APPLICATIONS PANEL ────────────────────────────────────────────────────────
const ApplicationsPanel = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchApps = useCallback(async () => {
    setLoading(true);
    try {
      const res = await writeFetch('read/merchant_applications_1741165000?limit=200');
      const data = await res.json();
      if (data.status === 'success') {
        const sorted = (data.data || []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setApps(sorted);
      }
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchApps(); }, [fetchApps]);

  const handleApprove = async (app) => {
    try {
      await writeFetch(`update/merchant_applications_1741165000/${app.id}`, {
        method: 'PUT', body: JSON.stringify({ status: 'approved' })
      });
      const access_code = Math.floor(100000 + Math.random() * 900000).toString();
      await writeFetch(`create/merchant_onboarding_codes_1776600001`, {
        method: 'POST', body: JSON.stringify({
          id: crypto.randomUUID(),
          owner_id: app.owner_id,
          business_name: app.company_name || app.shop_name,
          contact_email: app.email,
          access_code: access_code,
          status: 'active',
          created_at: new Date().toISOString()
        })
      });
      
      // Also update local mock data if exists
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('mock_provider_')) {
            const p = JSON.parse(localStorage.getItem(key));
            if (p.owner_id === app.owner_id) {
                p.status = 'active';
                localStorage.setItem(key, JSON.stringify(p));
            }
        }
      }
      
      fetchApps();
    } catch {}
  };

  const handleReject = async (id) => {
    try {
      await writeFetch(`update/merchant_applications_1741165000/${id}`, {
        method: 'PUT', body: JSON.stringify({ status: 'rejected' })
      });
      fetchApps();
    } catch {}
  };

  const pending = apps.filter(a => a.status === 'pending');
  const processed = apps.filter(a => a.status !== 'pending');

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-accent-gold border-t-accent-navy rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <SectionCard icon={FiBriefcase} title="Pending Applications" subtitle="Awaiting review" badge={pending.length}>
        {pending.length === 0 ? (
          <div className="py-16 text-center bg-brand-light rounded-[24px] border-2 border-dashed border-brand-dark">
            <SafeIcon icon={FiCheckCircle} className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h4 className="text-lg font-serif text-accent-navy mb-1">All Caught Up</h4>
            <p className="text-text-muted text-sm font-light">No new merchant applications.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {pending.map(app => (
              <div key={app.id} className="bg-white p-5 rounded-[24px] border border-brand-dark flex flex-col md:flex-row justify-between gap-4">
                <div>
                  <h4 className="font-bold text-accent-navy text-lg">{app.company_name || app.shop_name}</h4>
                  <div className="flex items-center gap-4 mt-2 text-sm text-text-muted">
                    <span className="flex items-center gap-1"><SafeIcon icon={FiMail} className="w-4 h-4" /> {app.email}</span>
                  </div>
                  <p className="mt-3 text-sm text-text-muted">{app.description}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleReject(app.id)} className="p-3 bg-brand-light text-red-500 rounded-xl hover:bg-red-50 transition-all h-fit">
                    <SafeIcon icon={FiX} className="w-5 h-5" />
                  </button>
                  <button onClick={() => handleApprove(app)} className="px-5 py-3 bg-green-500 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-green-600 transition-all flex items-center gap-2 h-fit">
                    <SafeIcon icon={FiCheckCircle} className="w-4 h-4" /> Approve
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
      
      {processed.length > 0 && (
        <SectionCard icon={FiCheckCircle} title="Processed Applications" subtitle="History">
          <div className="space-y-3">
            {processed.map(app => (
              <div key={app.id} className="flex items-center justify-between p-4 bg-brand-light rounded-2xl border border-brand-dark">
                <div>
                  <p className="font-bold text-accent-navy">{app.company_name || app.shop_name}</p>
                  <p className="text-xs text-text-muted">{app.email}</p>
                </div>
                <StatusBadge status={app.status} />
              </div>
            ))}
          </div>
        </SectionCard>
      )}
    </div>
  );
};

// ── ACTIVE MERCHANTS PANEL ────────────────────────────────────────────────────
const ActiveMerchantsPanel = () => {
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMerchants = useCallback(async () => {
    setLoading(true);
    let allMerchants = [];
    try {
      // Removing sort params directly from NCB query to prevent silent failures for unindexed fields
      const res = await writeFetch('read/merchant_onboarding_codes_1776600001?limit=200');
      const data = await res.json();
      if (data.status === 'success') {
        allMerchants = data.data || [];
      }
    } catch {}

    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('mock_provider_')) {
            try {
                const p = JSON.parse(localStorage.getItem(key));
                if (!allMerchants.find(x => x.owner_id === p.owner_id)) {
                    allMerchants.push({
                        id: p.id,
                        owner_id: p.owner_id,
                        business_name: p.name,
                        contact_email: p.email || 'mock@example.com',
                        access_code: 'MOCK-123',
                        status: p.status || 'active',
                        created_at: new Date().toISOString()
                    });
                }
            } catch (e) {}
        }
    }

    setMerchants(allMerchants.sort((a,b) => new Date(b.created_at) - new Date(a.created_at)));
    setLoading(false);
  }, []);

  useEffect(() => { fetchMerchants(); }, [fetchMerchants]);

  const toggleStatus = async (m) => {
    const newStatus = m.status === 'active' ? 'suspended' : 'active';
    try {
      await writeFetch(`update/merchant_onboarding_codes_1776600001/${m.id}`, {
        method: 'PUT', body: JSON.stringify({ status: newStatus })
      });
      fetchMerchants();
    } catch {}
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-accent-gold border-t-accent-navy rounded-full animate-spin" /></div>;

  return (
    <SectionCard icon={FiUsers} title="Active Merchants" subtitle="Manage approved vendors">
      {merchants.length === 0 ? (
        <div className="py-16 text-center bg-brand-light rounded-[24px] border-2 border-dashed border-brand-dark">
          <SafeIcon icon={FiShoppingBag} className="w-12 h-12 text-text-muted mx-auto mb-4" />
          <h4 className="text-lg font-serif text-accent-navy mb-1">No Merchants Yet</h4>
          <p className="text-text-muted text-sm font-light">Approved applications will appear here.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {merchants.map(m => (
            <div key={m.id} className="bg-white p-5 rounded-[24px] border border-brand-dark shadow-sm">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h4 className="font-bold text-accent-navy text-lg">{m.business_name}</h4>
                  <p className="text-xs text-text-muted mt-1">{m.contact_email}</p>
                </div>
                <StatusBadge status={m.status} />
              </div>
              <div className="flex items-center justify-between pt-4 border-t border-brand-light">
                <div className="bg-brand-light px-3 py-1.5 rounded-lg border border-brand-dark">
                  <p className="text-[9px] font-black uppercase tracking-widest text-text-muted mb-0.5">Access Code</p>
                  <p className="text-sm font-mono font-bold text-accent-navy tracking-widest">{m.access_code}</p>
                </div>
                <button onClick={() => toggleStatus(m)} className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${
                  m.status === 'active' ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-green-50 text-green-600 hover:bg-green-100'
                }`}>
                  {m.status === 'active' ? 'Suspend' : 'Reactivate'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
};

// ── COMMISSION RATES PANEL ────────────────────────────────────────────────────
const CommissionPanel = () => {
  const [rates, setRates] = useState(loadCommission());
  const [baseRate, setBaseRate] = useState(rates.base || 5);

  const handleSave = () => {
    const newRates = { ...rates, base: baseRate };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newRates));
    alert('Commission rates saved successfully.');
  };

  const updateCatRate = (id, val) => {
    setRates(prev => ({ ...prev, [id]: val }));
  };

  return (
    <div className="space-y-6">
      <SectionCard icon={FiPercent} title="Base Commission" subtitle="Default platform fee">
        <div className="flex items-center gap-6 bg-brand-light p-6 rounded-[24px] border border-brand-dark">
          <div className="flex-1">
            <h4 className="font-bold text-accent-navy">Global Rate</h4>
            <p className="text-sm text-text-muted">Applied to all sales unless a category override exists.</p>
          </div>
          <div className="flex items-center gap-2">
            <input type="number" value={baseRate} onChange={e => setBaseRate(Number(e.target.value))} className="w-20 px-4 py-3 bg-white border border-brand-dark rounded-xl text-center font-bold text-accent-navy focus:border-accent-gold outline-none" />
            <span className="text-lg font-bold text-text-muted">%</span>
          </div>
        </div>
      </SectionCard>

      <SectionCard icon={FiSliders} title="Category Overrides" subtitle="Specific rates by vertical">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {CATEGORY_RATES.map(cat => {
            const val = rates[cat.id] !== undefined ? rates[cat.id] : cat.rate;
            return (
              <div key={cat.id} className="flex items-center justify-between p-4 bg-white rounded-2xl border border-brand-dark">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${cat.color}`} />
                  <p className="font-bold text-sm text-accent-navy">{cat.label}</p>
                </div>
                <div className="flex items-center gap-2">
                  <input type="number" value={val} onChange={e => updateCatRate(cat.id, Number(e.target.value))} className="w-16 px-2 py-1.5 bg-brand-light border border-brand-dark rounded-lg text-center text-sm font-bold text-accent-navy focus:border-accent-gold outline-none" />
                  <span className="text-sm font-bold text-text-muted">%</span>
                </div>
              </div>
            );
          })}
        </div>
        <div className="pt-6 border-t border-brand-light flex justify-end">
          <button onClick={handleSave} className="px-8 py-3 bg-accent-navy text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-gold transition-all shadow-md flex items-center gap-2">
            <SafeIcon icon={FiSave} className="w-4 h-4" /> Save Rates
          </button>
        </div>
      </SectionCard>
    </div>
  );
};

const SUB_TABS = [
  { id: 'applications', label: 'Applications',    icon: FiBriefcase   },
  { id: 'merchants',    label: 'Active Merchants', icon: FiShoppingBag },
  { id: 'products',     label: 'Product Approvals', icon: FiPackage },
  { id: 'bookings',     label: 'Booking Oversight', icon: FiCalendar },
  { id: 'commission',   label: 'Commission Rates', icon: FiPercent     },
];

const MerchantManagement = () => {
  const [activeSubTab, setActiveSubTab] = useState('applications');

  return (
    <div className="space-y-8">
      <div className="flex overflow-x-auto hide-scrollbar gap-2 pb-1">
        {SUB_TABS.map(t => (
          <button key={t.id} onClick={() => setActiveSubTab(t.id)}
            className={`flex items-center gap-2 px-6 py-3 rounded-full text-[10px] font-black uppercase tracking-widest border-2 whitespace-nowrap transition-all ${
              activeSubTab === t.id
                ? 'bg-accent-navy border-accent-navy text-white'
                : 'bg-white border-brand-dark text-text-muted hover:border-accent-gold'
            }`}>
            <SafeIcon icon={t.icon} className="w-3.5 h-3.5" />
            {t.label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div key={activeSubTab}
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
          {activeSubTab === 'applications' && <ApplicationsPanel />}
          {activeSubTab === 'merchants'    && <ActiveMerchantsPanel />}
          {activeSubTab === 'products'     && <ProductApprovalsPanel />}
          {activeSubTab === 'bookings'     && <BookingOversightPanel />}
          {activeSubTab === 'commission'   && <CommissionPanel />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default MerchantManagement;